import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PetitionModal from "../components/PetitionModal";
import SubscribeModal from "../components/SubscribeModal";
import { PETITION_FALLBACK_COUNT, PETITION_URL } from "./constants";

/**
 * PetitionContext
 *
 * Global state that (a) exposes the current signature count fetched from
 * our Netlify function scraping Change.org, and (b) lets any "Sign the
 * Petition" button on the site open a shared in-site modal instead of
 * a new browser tab.
 *
 * How it works in different environments:
 *   • On Netlify production   → GET /api/petition-count hits our serverless
 *                               function, which scrapes Change.org server-
 *                               side (bypassing CORS) and returns the live
 *                               count. Refreshed every 5 min + on tab focus.
 *   • On dev/preview          → Our Netlify function isn't running. As a
 *                               fallback, we try a public CORS proxy to
 *                               scrape Change.org directly from the browser
 *                               so the number still shows in preview.
 *   • Both fail               → Show the PETITION_FALLBACK_COUNT constant.
 */

const PetitionContext = createContext(null);

const CHANGE_ORG_PETITION_PAGE =
    "https://www.change.org/p/pass-jc-law-require-aeds-cpr-training-in-hotels";

function extractSignatureCount(html) {
    if (!html || typeof html !== "string") return null;
    const patterns = [
        /"signatureCount"\s*:\s*\{\s*"displayed"\s*:\s*(\d+)/i,
        /"signatureCount"\s*:\s*\{[^}]*?"total"\s*:\s*(\d+)/i,
        /"signatureCount"\s*:\s*\{[^}]*?"displayed"\s*:\s*(\d+)/i,
        /"signature_count"\s*:\s*(\d+)/i,
        /"signatureCount"\s*:\s*(\d+)(?![\d.])/i,
        /([\d,]+)\s+have\s+signed/i,
    ];
    for (const re of patterns) {
        const m = html.match(re);
        if (m && m[1]) {
            const n = parseInt(String(m[1]).replace(/,/g, ""), 10);
            if (!Number.isNaN(n) && n >= 0) return n;
        }
    }
    return null;
}

export function PetitionProvider({ children }) {
    // Start with the fallback constant so the UI shows a real number
    // immediately, then replace with the true live value once fetched.
    const [count, setCount] = useState(PETITION_FALLBACK_COUNT);
    const [modalOpen, setModalOpen] = useState(false);
    const [triggerRef, setTriggerRef] = useState(null);

    // Subscribe modal (site-wide "Subscribe" button dialog)
    const [subscribeOpen, setSubscribeOpen] = useState(false);
    const [subscribeTriggerRef, setSubscribeTriggerRef] = useState(null);

    // Fetch the count from all available sources; auto-refresh every 5 minutes.
    useEffect(() => {
        let cancelled = false;

        const fetchWithTimeout = async (url, opts = {}, timeoutMs = 6000) => {
            const controller = new AbortController();
            const t = setTimeout(() => controller.abort(), timeoutMs);
            try {
                const res = await fetch(url, { ...opts, signal: controller.signal });
                return res;
            } finally {
                clearTimeout(t);
            }
        };

        const tryNetlifyFunction = async () => {
            try {
                const res = await fetchWithTimeout(
                    "/api/petition-count",
                    { headers: { Accept: "application/json" } },
                    6000
                );
                if (!res.ok) return null;
                const data = await res.json();
                return typeof data.count === "number" ? data.count : null;
            } catch (e) {
                return null;
            }
        };

        // Try a couple of CORS proxies as a last-resort fallback for dev preview.
        const CORS_PROXIES = [
            (u) => `https://corsproxy.io/?url=${encodeURIComponent(u)}`,
            (u) => `https://api.allorigins.win/raw?url=${encodeURIComponent(u)}`,
        ];

        const tryCorsProxy = async () => {
            for (const proxy of CORS_PROXIES) {
                try {
                    const res = await fetchWithTimeout(
                        proxy(CHANGE_ORG_PETITION_PAGE),
                        { headers: { Accept: "text/html" } },
                        8000
                    );
                    if (!res.ok) continue;
                    const html = await res.text();
                    const n = extractSignatureCount(html);
                    if (n !== null) return n;
                } catch (e) {
                    /* try next proxy */
                }
            }
            return null;
        };

        const load = async () => {
            // Primary: our Netlify serverless function (works on production).
            let n = await tryNetlifyFunction();
            // Fallback: public CORS proxies (used in dev preview or if Netlify fn fails).
            if (n === null) n = await tryCorsProxy();
            if (cancelled) return;
            if (n === null) return; // keep the previous / fallback value
            setCount((prev) => (prev === n ? prev : n));
        };

        load();
        const intervalId = setInterval(load, 5 * 60 * 1000);
        const onVisible = () => {
            if (document.visibilityState === "visible") load();
        };
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, []);

    const openPetition = useCallback((originRef = null) => {
        setTriggerRef(originRef);
        setModalOpen(true);
    }, []);

    const closePetition = useCallback(() => {
        setModalOpen(false);
    }, []);

    const openSubscribe = useCallback((originRef = null) => {
        setSubscribeTriggerRef(originRef);
        setSubscribeOpen(true);
    }, []);

    const closeSubscribe = useCallback(() => {
        setSubscribeOpen(false);
    }, []);

    // Global click interceptor:
    //   Any <a href="…change.org/p/pass-jc-law…"> anywhere on the site is
    //   intercepted so it opens the shared in-site modal instead of
    //   navigating away / opening a new tab. Users hold Ctrl / Cmd /
    //   middle-click to bypass and open in a new tab as expected.
    useEffect(() => {
        // Compare on the pathname to be resilient to different query strings.
        const petitionPath = (() => {
            try {
                return new URL(PETITION_URL).pathname;
            } catch (e) {
                return "/p/pass-jc-law-require-aeds-cpr-training-in-hotels";
            }
        })();

        const isPetitionLink = (href) => {
            if (!href) return false;
            try {
                const u = new URL(href, window.location.origin);
                return (
                    u.hostname.endsWith("change.org") &&
                    u.pathname === petitionPath
                );
            } catch (e) {
                return false;
            }
        };

        const onClick = (e) => {
            // Ignore modified clicks so users can open in a new tab if they want.
            if (
                e.defaultPrevented ||
                e.button !== 0 ||
                e.metaKey ||
                e.ctrlKey ||
                e.shiftKey ||
                e.altKey
            ) {
                return;
            }
            const anchor = e.target.closest && e.target.closest("a[href]");
            if (!anchor) return;
            if (!isPetitionLink(anchor.getAttribute("href"))) return;
            e.preventDefault();
            openPetition({ current: anchor });
        };

        document.addEventListener("click", onClick);
        return () => document.removeEventListener("click", onClick);
    }, [openPetition]);

    const value = useMemo(
        () => ({
            count,
            openPetition,
            closePetition,
            openSubscribe,
            closeSubscribe,
        }),
        [count, openPetition, closePetition, openSubscribe, closeSubscribe]
    );

    return (
        <PetitionContext.Provider value={value}>
            {children}
            <PetitionModal
                isOpen={modalOpen}
                onClose={closePetition}
                count={count}
                triggerRef={triggerRef}
            />
            <SubscribeModal
                isOpen={subscribeOpen}
                onClose={closeSubscribe}
                triggerRef={subscribeTriggerRef}
            />
        </PetitionContext.Provider>
    );
}

export function usePetition() {
    const ctx = useContext(PetitionContext);
    if (!ctx) {
        throw new Error("usePetition must be used within a PetitionProvider");
    }
    return ctx;
}

// Alias for readability — the context now exposes both petition and
// subscribe controls, but we keep the original hook name to avoid churn.
export const useAppUI = usePetition;

/**
 * Helper: click handler factory for any "Sign the Petition" button.
 * Prevents default anchor navigation and opens the modal instead.
 * Pass `ref` to restore focus to the trigger on close.
 */
export function makePetitionClickHandler(openPetition, ref = null) {
    return (e) => {
        e.preventDefault();
        openPetition(ref);
    };
}
